import { Link } from "@inertiajs/react"
import { ChevronRight, X, type LucideIcon } from "lucide-react"
import type { ScopeNode } from "@/components/ChannelMenu"

interface Props {
  scopeTree: ScopeNode[]
  scopeId: string
  marketId: string
  /** Href that scopes the page to one node (the market slug means the whole market). */
  hrefForScope: (slug: string) => string
  /** Words in front of the path, e.g. "Scope:". */
  label?: string
  /** Shown in place of the label words; the label becomes its tooltip. */
  icon?: LucideIcon
  /** Shows an [x] after the path that goes back to the top node. */
  clearable?: boolean
  className?: string
}

/**
 * Path from the market node down to the drilled-in scope. Every step above the
 * current one is a link, so going back up is one click, without opening the
 * Scope menu. Renders nothing at the market level.
 */
export default function ScopeBreadcrumb({
  scopeTree, scopeId, marketId, hrefForScope, label, icon: Icon, clearable = false, className = "border-b px-4 py-3",
}: Props) {
  if (scopeId === marketId) return null

  const byId = new Map(scopeTree.map((n) => [n.id, n]))
  const path: ScopeNode[] = []
  for (let node = byId.get(scopeId); node && node.id !== marketId; node = node.parent_id ? byId.get(node.parent_id) : undefined) {
    path.unshift(node)
  }
  const market = byId.get(marketId)
  if (!market || path.length === 0) return null

  const steps = [{ node: market, label: market.name }, ...path.map((node) => ({ node, label: node.name }))]
  const last = steps.length - 1

  return (
    <nav aria-label={label ? `${label} path` : "Channel path"} className={`flex flex-wrap items-center gap-1 text-sm ${className}`}>
      {Icon ? (
        <span title={label} aria-label={label} className="mr-0.5 shrink-0 text-muted-foreground">
          <Icon size={14} />
        </span>
      ) : (
        label && <span className="w-20 shrink-0 text-muted-foreground">{label}:</span>
      )}
      {steps.map(({ node, label }, i) => (
        <span key={node.id} className="flex items-center gap-1">
          {i > 0 && <ChevronRight size={14} className="text-muted-foreground" />}
          {i === last ? (
            <span className="font-medium">{label}</span>
          ) : (
            <Link href={hrefForScope(node.slug)} className="text-link hover:underline">
              {label}
            </Link>
          )}
        </span>
      ))}
      {clearable && (
        <Link
          href={hrefForScope(market.slug)}
          aria-label={`Remove ${label ?? "scope"} filter`}
          title="Remove filter"
          className="ml-0.5 rounded-sm p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          <X size={12} strokeWidth={2.25} />
        </Link>
      )}
    </nav>
  )
}
